import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { adminApi } from './AdminLayout';
import DeploymentPanel from './DeploymentPanel';
import FeeSchedulePanel from './FeeSchedulePanel';
import ReviewActions from './ReviewActions';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

const STATUS_COLORS = {
  pending: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  approved: 'bg-green-500/20 text-green-300 border-green-500/30',
  initiated: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  in_review: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  submitted: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  rejected: 'bg-red-500/20 text-red-300 border-red-500/30',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const Field = ({ label, value }) => (
  <div>
    <dt className="text-xs text-slate-400">{label}</dt>
    <dd className="text-sm text-slate-200 mt-0.5">{value || '-'}</dd>
  </div>
);

const Section = ({ title, children }) => (
  <div className="border-b border-slate-700 pb-4 last:border-0 last:pb-0">
    <h4 className="text-sm font-medium text-slate-300 mb-3">{title}</h4>
    <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
      {children}
    </dl>
  </div>
);

const formatAddress = (addr) => {
  if (!addr) return '-';
  const parts = [addr.street, addr.suite, addr.city, addr.state, addr.zip].filter(Boolean);
  return parts.join(', ') || '-';
};

const ApplicationDetail = () => {
  const { token } = useParams();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchApplication = useCallback(async () => {
    try {
      const response = await adminApi.get(`/admin/applications/${token}`);
      setApplication(response.data);
    } catch (error) {
      toast.error('Failed to load application');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchApplication();
  }, [fetchApplication]);

  const handleUpdate = (updated) => {
    if (updated) {
      setApplication((prev) => ({ ...prev, ...updated }));
    } else {
      fetchApplication();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-slate-400">Loading application...</p>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-slate-400">Application not found</p>
      </div>
    );
  }

  const company = application.company || {};
  const dba = application.dba || {};
  const contact = application.corporateContact || {};
  const processing = application.processing || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/applications"
            className="text-slate-400 hover:text-slate-200 transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-100">
              {company.name || application.companyName || 'Application'}
            </h1>
            <p className="text-sm text-slate-400 mt-0.5">
              Token: {token} | Created: {formatDate(application.createdAt)}
            </p>
          </div>
        </div>
        <Badge className={`text-sm px-3 py-1 ${STATUS_COLORS[application.status] || 'bg-slate-600 text-slate-300'} border`}>
          {(application.status || 'unknown').replace('_', ' ')}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-slate-800 rounded-lg p-6 border border-slate-700 space-y-4">
            <h3 className="text-lg font-semibold text-slate-100">Application Summary</h3>

            <Section title="Contact Info">
              <Field label="First Name" value={application.firstName} />
              <Field label="Last Name" value={application.lastName} />
              <Field label="Email" value={application.email} />
              <Field label="Phone" value={application.phone} />
            </Section>

            <Section title="Company">
              <Field label="Legal Name" value={company.name} />
              <Field label="Tax ID" value={company.taxId} />
              <Field label="Type" value={company.type} />
              <Field label="Website" value={company.website || application.website} />
              <Field label="Address" value={formatAddress(company.address)} />
              <Field label="State of Inc." value={company.stateOfIncorporation} />
            </Section>

            {dba.name && (
              <Section title="DBA">
                <Field label="DBA Name" value={dba.name} />
                <Field label="Address" value={formatAddress(dba.address)} />
              </Section>
            )}

            <Section title="Processing">
              <Field label="Method" value={application.processingMethod} />
              <Field label="Monthly Volume" value={processing.volumes?.monthly ? `$${Number(processing.volumes.monthly).toLocaleString()}` : '-'} />
              <Field label="Avg Ticket" value={processing.volumes?.averageTicket ? `$${processing.volumes.averageTicket}` : '-'} />
              <Field label="High Ticket" value={processing.volumes?.highTicket ? `$${processing.volumes.highTicket}` : '-'} />
            </Section>

            {application.principals?.length > 0 && (
              <Section title={`Principals (${application.principals.length})`}>
                {application.principals.map((p, i) => (
                  <React.Fragment key={i}>
                    <Field label={`Principal ${i + 1}`} value={`${p.firstName || ''} ${p.lastName || ''}`.trim() || '-'} />
                    <Field label="Ownership" value={p.ownershipPercentage ? `${p.ownershipPercentage}%` : '-'} />
                  </React.Fragment>
                ))}
              </Section>
            )}

            {application.reviewNotes && (
              <Section title="Review Notes">
                <div className="col-span-2">
                  <p className="text-sm text-slate-300 whitespace-pre-wrap">{application.reviewNotes}</p>
                  {application.reviewedBy && (
                    <p className="text-xs text-slate-500 mt-1">By: {application.reviewedBy} at {formatDate(application.reviewedAt)}</p>
                  )}
                </div>
              </Section>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <ReviewActions
            token={token}
            application={application}
            onUpdate={handleUpdate}
          />
          <DeploymentPanel
            token={token}
            deployment={application.deployment}
            onUpdate={handleUpdate}
          />
          <FeeSchedulePanel
            token={token}
            feeSchedule={application.feeSchedule}
            onUpdate={handleUpdate}
          />
        </div>
      </div>
    </div>
  );
};

export default ApplicationDetail;
